// IcebergExperience — web-port 1-op-1 van src/features/iceberg/IcebergExperience.tsx
// Drie-lagige reveal: Boven water (wat je zegt) · Waterlijn (wat je doet) ·
// Onder water (wat IMMO MATCH begrijpt). Fictieve demo-persona "Sarah" + Tom.
import React, { useEffect, useState } from 'react';
import Icon from './Icon';
import { useDemoTracking } from './useDemoTracking';

const NAVY = '#0e2340';
const GOLD = '#c48a3a';
const GOLD_SOFT = 'rgba(196,138,58,0.14)';
const CREME = '#f5eee1';
const WATER = '#38a3d1';
const INK = '#0e2340';
const INK_MUTED = '#5a6b7d';

const LAYER_TOP = {
  eyebrow: 'BOVEN WATER',
  title: 'Wat je zegt dat je zoekt',
  subtitle: 'De filters die je zelf hebt ingesteld — je bewuste voorkeuren.',
  items: [
    { icon: 'target', label: 'Budget', value: '€ 400.000' },
    { icon: 'map-pin', label: 'Regio', value: 'Gent — max 15 km' },
    { icon: 'home', label: 'Type', value: 'Karakterhuis' },
    { icon: 'grid', label: 'Slaapkamers', value: '3+' },
    { icon: 'zap', label: 'EPC', value: 'B of beter' },
    { icon: 'sun', label: 'Extra', value: 'Tuin, rustige buurt' },
  ],
};

const LAYER_MID = {
  eyebrow: 'AAN DE WATERLIJN',
  title: 'Wat je doet',
  subtitle: 'De signalen die IMMO MATCH observeert tijdens je woonreis.',
  items: [
    { icon: 'eye', label: 'Bekeken', hint: '17 woningen — waarvan 6 méér dan 1 keer', pct: 0.78 },
    { icon: 'heart', label: 'Bewaard', hint: '5 woningen — 4 met tuin > 100 m²', pct: 0.86 },
    { icon: 'x-circle', label: 'Weggeveegd', hint: '11 woningen — 8 zonder buitenruimte', pct: 0.62 },
    { icon: 'sliders', label: 'Filters aangepast', hint: 'Budget +8% verhoogd toen match sterk was', pct: 0.5 },
    { icon: 'clock', label: 'Reactietijd', hint: 'Reageert binnen 2u op nieuwe TopMatches', pct: 0.9 },
    { icon: 'users', label: 'Partneractiviteit', hint: 'Tom bewaart dezelfde 4 woningen', pct: 0.94 },
    { icon: 'calendar', label: 'Bezoek-aanvragen', hint: '2 bezoeken samen gepland', pct: 0.7 },
  ],
};

const LAYER_BOTTOM = {
  eyebrow: 'ONDER WATER',
  title: 'Wat IMMO MATCH begrijpt',
  subtitle: 'Het woonprofiel dat je gedrag onthult — méér dan je filters ooit vertellen.',
  insights: [
    { icon: 'trending-up', label: 'Hoge koopintentie', body: 'Snelle reactietijd + gedeelde favorieten + budget-flex bij goede match.' },
    { icon: 'map', label: 'Voorkeur voor rustige zijstraten', body: '9 van de 10 bewaarde woningen liggen in kleinere straten, niet op de invalsassen.' },
    { icon: 'tool', label: 'Renovatiebereidheid', body: 'Woningen met EPC C worden niet automatisch afgewezen — mits karakter aanwezig.' },
    { icon: 'sun', label: 'Buitenruimte is een must, geen wens', body: 'Elke NO-swipe was op appartementen zonder terras of tuin.' },
    { icon: 'heart', label: 'Gezamenlijk beslissingsprofiel', body: 'Convergerende smaak: karakterhuizen + station-bereikbaar. 94% overlap.' },
  ],
};

const CONCLUSION = {
  eyebrow: 'HET RESULTAAT',
  title: 'Daarom krijg je andere matches dan op klassieke sites',
  body: 'Een gewone zoekmachine vindt woningen die aan jouw filters voldoen. IMMO MATCH vindt woningen die aan jouw wóónprofiel voldoen — de combinatie van wat je zegt én wat je doet.',
};

export function IcebergExperience({ onAppCta, onGhostDemo }) {
  const { track } = useDemoTracking('iceberg');
  const [layer, setLayer] = useState(0);

  useEffect(() => {
    track('demo_started', { module: 'iceberg' });
    track('iceberg_opened');
  }, []); // eslint-disable-line

  function reveal(next) {
    if (next <= layer) return;
    setLayer(next);
    if (next === 1) track('iceberg_layer_revealed', { layer_index: 1, layer_name: 'behavior' });
    if (next === 2) track('iceberg_layer_revealed', { layer_index: 2, layer_name: 'insights' });
    if (next === 3) {
      track('iceberg_layer_revealed', { layer_index: 3, layer_name: 'conclusion' });
      track('iceberg_completed');
    }
  }

  return (
    <div style={st.root}>
      <div style={st.scroll}>
        {/* ── Kop ── */}
        <div style={st.header}>
          <div style={st.brandPill}>
            <span style={st.brandTxt}>IMMO<span style={{ color: GOLD, fontStyle: 'italic', fontWeight: 400 }}>match</span></span>
            <span style={st.pill}><span style={st.pillTxt}>DEMO · IJSBERGMETHODE</span></span>
          </div>
          <div style={st.headline}>Waarom IMMO MATCH andere matches vindt dan klassieke woningsites.</div>
          <div style={st.subline}>Je woonwensen zijn maar het topje van de ijsberg. Ontdek wat er onder water zit.</div>
        </div>

        {/* ── Laag 1 — Boven water ── */}
        <div style={{ ...st.layerCard, ...st.layerTop }}>
          <div style={st.eyebrow}>{LAYER_TOP.eyebrow}</div>
          <div style={st.layerTitle}>{LAYER_TOP.title}</div>
          <div style={st.layerSub}>{LAYER_TOP.subtitle}</div>
          <div style={st.chipsWrap}>
            {LAYER_TOP.items.map((it, i) => (
              <div key={i} style={st.chip}>
                <Icon name={it.icon} size={12} color={INK} />
                <div>
                  <div style={st.chipLabel}>{it.label}</div>
                  <div style={st.chipValue}>{it.value}</div>
                </div>
              </div>
            ))}
          </div>
          {layer === 0 ? (
            <button className="immo-btn" style={st.revealBtn} onClick={() => reveal(1)} data-testid="iceberg-reveal-1">
              <Icon name="chevrons-down" size={16} color={NAVY} />
              <span style={st.revealBtnTxt}>Ontdek wat onder water zit</span>
            </button>
          ) : null}
        </div>

        {/* ── Waterlijn ── */}
        <div style={st.waterlineWrap}>
          <div style={st.waterlineDash} />
          <span style={st.waterlineTxt}>WATERLIJN — hier stopt een klassieke zoekmachine</span>
          <div style={st.waterlineDash} />
        </div>

        {/* ── Laag 2 — Wat je doet ── */}
        <div style={{
          ...st.layerCard, ...st.layerMid,
          opacity: layer >= 1 ? 1 : 0,
          transform: layer >= 1 ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity .7s ease, transform .7s cubic-bezier(.2,.8,.3,1)',
        }}>
          <div style={{ ...st.eyebrow, color: WATER }}>{LAYER_MID.eyebrow}</div>
          <div style={st.layerTitleLight}>{LAYER_MID.title}</div>
          <div style={st.layerSubLight}>{LAYER_MID.subtitle}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
            {LAYER_MID.items.map((it, i) => (
              <div key={i} style={st.behavRow}>
                <div style={st.behavIcon}><Icon name={it.icon} size={14} color={WATER} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={st.behavLabel}>{it.label}</span>
                    <span style={st.behavPct}>{Math.round(it.pct * 100)}%</span>
                  </div>
                  <div style={st.behavHint}>{it.hint}</div>
                  <div style={st.progressBg}>
                    <div style={{ ...st.progressFg, width: `${Math.round(it.pct * 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {layer === 1 ? (
            <button className="immo-btn" style={st.revealBtn} onClick={() => reveal(2)} data-testid="iceberg-reveal-2">
              <Icon name="chevrons-down" size={16} color={NAVY} />
              <span style={st.revealBtnTxt}>Duik dieper — wat IMMO MATCH begrijpt</span>
            </button>
          ) : null}
        </div>

        {/* ── Laag 3 — Onder water ── */}
        <div style={{
          ...st.layerCard, ...st.layerBottom,
          opacity: layer >= 2 ? 1 : 0,
          transform: layer >= 2 ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity .8s ease .2s, transform .8s cubic-bezier(.2,.8,.3,1) .2s',
        }}>
          <div style={{ ...st.eyebrow, color: GOLD }}>{LAYER_BOTTOM.eyebrow}</div>
          <div style={st.layerTitleLight}>{LAYER_BOTTOM.title}</div>
          <div style={st.layerSubLight}>{LAYER_BOTTOM.subtitle}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            {LAYER_BOTTOM.insights.map((it, i) => (
              <div key={i} style={st.insightCard}>
                <div style={st.insightIcon}><Icon name={it.icon} size={14} color={GOLD} /></div>
                <div style={{ flex: 1 }}>
                  <div style={st.insightLabel}>{it.label}</div>
                  <div style={st.insightBody}>{it.body}</div>
                </div>
              </div>
            ))}
          </div>
          {layer === 2 ? (
            <button className="immo-btn" style={{ ...st.revealBtn, background: GOLD, borderColor: GOLD }} onClick={() => reveal(3)} data-testid="iceberg-reveal-3">
              <Icon name="check-circle" size={16} color="#fff" />
              <span style={{ ...st.revealBtnTxt, color: '#fff' }}>Zie de conclusie</span>
            </button>
          ) : null}
        </div>

        {/* ── Conclusie + CTA ── */}
        {layer >= 3 ? (
          <div className="anim-fade-in" style={st.conclusion}>
            <div style={{ ...st.eyebrow, color: GOLD }}>{CONCLUSION.eyebrow}</div>
            <div style={st.conclTitle}>{CONCLUSION.title}</div>
            <div style={st.conclBody}>{CONCLUSION.body}</div>
            <div style={st.ctaBlock}>
              <button
                className="immo-btn"
                style={st.ctaPrimary}
                data-testid="iceberg-cta-app"
                onClick={() => {
                  track('app_cta_clicked', { from: 'iceberg_conclusion' });
                  if (onAppCta) onAppCta();
                  else window.location.href = '/go/demo?mode=solo&autoplay=1&v=2';
                }}
              >
                <Icon name="arrow-right-circle" size={17} color="#fff" />
                <span style={st.ctaPrimaryTxt}>Ontdek de IMMO MATCH-app</span>
              </button>
              <button
                className="immo-btn"
                style={{ padding: '6px 0' }}
                data-testid="iceberg-cta-ghost"
                onClick={() => {
                  track('ghost_demo_cta_clicked', { from: 'iceberg_conclusion' });
                  if (onGhostDemo) onGhostDemo();
                  else window.location.href = '/go/demo?mode=couple&autoplay=1&v=2';
                }}
              >
                <span style={st.ctaTextLinkTxt}>
                  Liever eerst kijken? <span style={st.ctaTextLinkAccent}>Bekijk de rondleiding →</span>
                </span>
              </button>
            </div>
            <div style={st.demoFooter}>Dit is een demo met fictieve data. Alle voorbeelden zijn illustratief.</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const st = {
  root: { background: `linear-gradient(180deg, ${NAVY}, #050a14)` },
  scroll: { padding: '32px 20px 80px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 780, width: '100%', margin: '0 auto', boxSizing: 'border-box' },

  header: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 },
  brandPill: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  brandTxt: { color: '#fff', fontSize: 22, fontWeight: 900, letterSpacing: 1.2 },
  pill: { background: GOLD_SOFT, border: '1px solid rgba(196,138,58,0.5)', padding: '5px 10px', borderRadius: 999 },
  pillTxt: { color: GOLD, fontSize: 10, fontWeight: 900, letterSpacing: 1.4 },
  headline: { color: '#fff', fontSize: 26, fontWeight: 900, lineHeight: '32px', letterSpacing: -0.4, marginTop: 6 },
  subline: { color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: '20px' },

  layerCard: { borderRadius: 22, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 },
  layerTop: { background: CREME },
  layerMid: { background: 'rgba(56,163,209,0.10)', border: '1px solid rgba(56,163,209,0.35)' },
  layerBottom: { background: 'rgba(196,138,58,0.08)', border: '1px solid rgba(196,138,58,0.35)' },

  eyebrow: { color: NAVY, fontSize: 11, fontWeight: 900, letterSpacing: 1.6 },
  layerTitle: { color: INK, fontSize: 20, fontWeight: 900, letterSpacing: -0.2 },
  layerTitleLight: { color: '#fff', fontSize: 20, fontWeight: 900, letterSpacing: -0.2 },
  layerSub: { color: INK_MUTED, fontSize: 13, lineHeight: '18px' },
  layerSubLight: { color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: '18px' },

  chipsWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  chip: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 12, padding: '8px 10px', border: '1px solid rgba(14,35,64,0.08)' },
  chipLabel: { color: INK_MUTED, fontSize: 9, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase' },
  chipValue: { color: INK, fontSize: 12.5, fontWeight: 800 },

  revealBtn: { marginTop: 14, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 999, padding: '10px 16px', border: '1px solid rgba(14,35,64,0.15)' },
  revealBtnTxt: { color: NAVY, fontSize: 13, fontWeight: 900, letterSpacing: 0.2 },

  waterlineWrap: { display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0' },
  waterlineDash: { flex: 1, height: 1, background: 'rgba(56,163,209,0.35)' },
  waterlineTxt: { color: WATER, fontSize: 9.5, fontWeight: 900, letterSpacing: 1.2 },

  behavRow: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  behavIcon: { width: 30, height: 30, borderRadius: 15, background: 'rgba(56,163,209,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(56,163,209,0.35)', flexShrink: 0 },
  behavLabel: { color: '#fff', fontSize: 13, fontWeight: 800 },
  behavPct: { color: WATER, fontSize: 12, fontWeight: 900 },
  behavHint: { color: 'rgba(255,255,255,0.65)', fontSize: 11.5, marginTop: 2, lineHeight: '15px' },
  progressBg: { marginTop: 6, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.10)' },
  progressFg: { height: 5, borderRadius: 3, background: WATER },

  insightCard: { display: 'flex', gap: 10, background: 'rgba(196,138,58,0.10)', borderRadius: 14, padding: 12, border: '1px solid rgba(196,138,58,0.28)' },
  insightIcon: { width: 30, height: 30, borderRadius: 15, background: 'rgba(196,138,58,0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(196,138,58,0.45)', flexShrink: 0 },
  insightLabel: { color: '#fff', fontSize: 13.5, fontWeight: 900 },
  insightBody: { color: 'rgba(255,255,255,0.75)', fontSize: 12.5, marginTop: 3, lineHeight: '17px' },

  conclusion: { marginTop: 12, background: '#fff', borderRadius: 22, padding: 22, display: 'flex', flexDirection: 'column', gap: 10 },
  conclTitle: { color: INK, fontSize: 22, fontWeight: 900, letterSpacing: -0.3, lineHeight: '28px' },
  conclBody: { color: INK_MUTED, fontSize: 13.5, lineHeight: '20px' },
  ctaBlock: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' },
  ctaPrimary: { display: 'flex', alignItems: 'center', gap: 10, background: NAVY, padding: '15px 22px', borderRadius: 999 },
  ctaPrimaryTxt: { color: '#fff', fontSize: 14.5, fontWeight: 900, letterSpacing: 0.3 },
  ctaTextLinkTxt: { color: INK_MUTED, fontSize: 12.5 },
  ctaTextLinkAccent: { color: GOLD, fontWeight: 800 },
  demoFooter: { color: INK_MUTED, fontSize: 11, marginTop: 8, fontStyle: 'italic' },
};
